'use client'

//__IMPORTS
import { useTranslations } from 'next-intl'
import { notFound, useParams } from 'next/navigation'

import CityJsonLd from '../../components/cityJsonLD'
import { useDestination } from '../../context/DestinationContext'
import { Branch, IBranch } from '../../types/schema'

import '@/styles/pages/branch.scss'

import { useMemo } from 'react'

import { BookingFormComponent } from '@/components/forms/booking'
import { ButtonBooking } from '@/components/home/btn-book'

import AddressInfo from './components/addressInfo'
import BranchLinks from './components/branchLinks'
import HorizontalGallery from './components/horizontalGallery'

//__HELPERS
const getCityByPath = (branches: IBranch[], cityPath: string) => {
  return branches.find((branch: IBranch) =>
    branch.branches.some(
      (branchItem) =>
        branchItem.city_slug?.toLowerCase() === cityPath?.toLowerCase() ||
        branch.code?.toLowerCase() === cityPath?.toLowerCase()
    )
  )
}

const getBranchByPath = (branches: Branch[], branchPath: string) => {
  return branches.find((branch: Branch) => branch.public_path.toLowerCase() === branchPath)
}

const getPageTitleKey = (
  city: string | string[] | undefined,
  branchName: string | undefined
): string => {
  const cityKey = typeof city === 'string' ? city.charAt(0).toUpperCase() + city.slice(1) : ''
  return branchName ? `${cityKey} : ${branchName}.SEO_PAGE_H1` : cityKey
}

export default function Branchs() {
  //__HOOKS & PARAMS
  const params = useParams()
  const t = useTranslations()
  const { data, isMobile, hasMounted } = useDestination()
  const cityPath = typeof params.city === 'string' ? params.city.toLowerCase() : ''
  const branchPath = typeof params.branch === 'string' ? params.branch.toLowerCase() : ''

  //__DATA
  const cityData = useMemo(() => {
    return data?.branches && getCityByPath(data.branches, cityPath)
  }, [data, cityPath])

  const branchData = useMemo(() => {
    return cityData?.branches && getBranchByPath(cityData.branches, branchPath)
  }, [cityData, branchPath])

  const sortedBranches = useMemo(() => {
    return (cityData?.branches || []).sort((a: Branch, b: Branch) => Number(b.id) - Number(a.id))
  }, [cityData])

  const pageTitle = useMemo(() => {
    return getPageTitleKey(params.city, branchData?.name_short)
  }, [params.city, branchData])

  //__ERROR STATE
  if (!branchData || !cityData) {
    notFound()
  }

  //__RENDER
  return (
    <div className='ui--branch'>
      <section className='ui--branch-section'>
        {hasMounted && !isMobile && <BookingFormComponent defaultBranchId={branchData?.id} />}

        <div className='content'>
          <header>
            <h1
              dangerouslySetInnerHTML={{
                __html: (() => {
                  try {
                    const translated = t(pageTitle)
                    return translated === pageTitle || translated.includes('missing translation')
                      ? branchData.name
                      : translated
                  } catch (error) {
                    return branchData.name
                  }
                })()
              }}
            />
          </header>

          <BranchLinks branches={sortedBranches} activeBranch={branchPath} />

          <div className='media-container z-150'>
            <div className='video-wrapper'>
              {branchData.images[0] &&
              branchData.images[0].toLowerCase().match(/\.mp4(\?.*)?$|\.mov(\?.*)?$/) ? (
                <video controls autoPlay preload='metadata'>
                  <source
                    src={branchData.images[0]}
                    type={
                      branchData.images[0].toLowerCase().includes('.mov')
                        ? 'video/quicktime'
                        : 'video/mp4'
                    }
                  />
                  <source src={branchData.images[0].replace('.mov', '.mp4')} type='video/mp4' />
                  Your browser does not support the video tag.
                </video>
              ) : (
                <img src={branchData.images[0]} alt={branchData.name} loading='lazy' />
              )}
              <div className='video-title'>{branchData.short_desc}</div>
            </div>
            <HorizontalGallery images={branchData.images?.slice(1) || []} />
            <div className='media-title'>
              <span>{branchData.name}</span>
            </div>
          </div>

          <h4>{branchData.long_desc}</h4>
          <div dangerouslySetInnerHTML={{ __html: branchData.map }} />

          <div className='info-container'>
            <AddressInfo
              data={{
                address: branchData.address,
                mapUrl: branchData.map_link,
                openHours: branchData.open_timename,
                closeHours: branchData.close_timename,
                phoneNumber: branchData.branch_phone
              }}
            />
            <div>
              <ButtonBooking branchId={branchData.id} />
              {branchData.menu && (
                <div className='my-3 bg-primary p-3 text-center text-lg font-bold text-white'>
                  <a href={branchData.menu} target='_blank' rel='noopener noreferrer'>
                    Gaaya Unique Menu
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
        <CityJsonLd branches={[branchData]} />
      </section>
    </div>
  )
}
